import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { BuyerCard } from 'src/components/Selectors/BuyerSelector/BuyerCard';
import { useCatalogDataContextEventsTransformedSelector } from 'src/contexts/CatalogDataContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useRetailSaleListingTickets } from 'src/hooks/api/useRetailSaleListingTickets';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { useRetailSaleContext } from 'src/modals/RetailSale/RetailSaleLocalContext';
import { OpenLinkIcon } from 'src/svgs/OpenLinkIcon';
import { DeleteIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import { getPerformerAndVenueForEvent } from 'src/utils/eventWithDataUtils';
import { getListingDetailsRelativeUrl } from 'src/utils/inventoryUtils';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  ListingQuery,
  RetailSaleListingTickets,
  Ticket,
} from 'src/WebApiController';

import { useSeatAutofill } from '../hooks/useSeatAutofill';
import { MultiListingSelectionRow, RetailSaleFormValues } from '../types';
import * as styles from './MultiListingRetailSaleCheckoutStep.css';
import { RetailSaleEarningsSection } from './RetailSaleCheckoutStep.components';
import { RetailSalePaymentSection } from './RetailSalePaymentSection';

/**
 * Checkout step for multi-listing retail sales (POS-8649).
 *
 * Renders a single unified table (SimpleTable pattern from
 * SalesGroupModal POS-8632, but reimplemented here — that branch is
 * unmerged) with one row per (listing + section + row) group. Each row
 * has an editable Qty input capped at the group's available quantity;
 * seatFrom/seatTo are auto-recomputed via useSeatAutofill.
 *
 * The flat `ticketIds` list expected by the future POS-8628 backend is
 * derived from `perListingSelections` at submit time (see
 * `buildRetailSaleWithTicketsInput`) — no shadow state to keep in sync.
 */
export const MultiListingRetailSaleCheckoutStep = () => {
  const { selectedBuyer, disabled } = useRetailSaleContext();

  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();

  const { watch, control, setValue } = useFormContext<RetailSaleFormValues>();
  const currencyCode = watch('currencyCode') ?? 'USD';
  const customerInfoMsg = useContent(ContentId.CustomerInfo);
  const loadingMsg = useContent(ContentId.LoadingTicketDetails);
  const noListingsMsg = useContent(ContentId.NoListingsSelected);

  const eventMsg = useContent(ContentId.Event);
  const listingMsg = useContent(ContentId.Listing);
  const sectionMsg = useContent(ContentId.Section);
  const rowMsg = useContent(ContentId.Row);
  const seatsMsg = useContent(ContentId.Seats);
  const qtyMsg = useContent(ContentId.Quantity);
  const unitCostMsg = useContent(ContentId.UnitCost);
  const unitProceedsMsg = useContent(ContentId.UnitProceeds);
  const lineTotalMsg = useContent(ContentId.LineTotal);
  const removeMsg = useContent(ContentId.Remove);
  const totalQuantityMsg = useContent(ContentId.TotalQuantity);
  const totalProceedsMsg = useContent(ContentId.TotalProceeds);

  const allListingIds = watch('allListingIds');
  const primaryListingId = watch('listingId');

  const filterQuery = useMemo((): ListingQuery | null => {
    const ids =
      allListingIds && allListingIds.length > 0
        ? allListingIds
        : primaryListingId && primaryListingId > 0
          ? [primaryListingId]
          : [];
    if (ids.length === 0) return null;
    return { ...EmptyListingQuery, entityIds: ids };
  }, [allListingIds, primaryListingId]);

  const { data: listingDetails, isLoading } =
    useRetailSaleListingTickets(filterQuery);

  const listingDetailsById = useMemo(() => {
    const map: Record<number, RetailSaleListingTickets> = {};
    listingDetails?.forEach((ld) => {
      map[ld.listingId] = ld;
    });
    return map;
  }, [listingDetails]);

  const { fields, remove } = useFieldArray({
    control,
    name: 'perListingSelections',
    // Row identity is stable across renders — react-hook-form auto-generates
    // a UUID via `id` on each field, so we don't need to hand-pick keyName.
  });

  // Summary aggregates — reactive to Qty and Unit Proceeds edits. Uses
  // `useWatch` (not `watch()`) because setValue on nested useFieldArray
  // paths doesn't reliably fire top-level `watch(fieldArrayName)`
  // subscribers, which is React Hook Form's known behavior for arrays.
  const watchedSelections = useWatch({
    control,
    name: 'perListingSelections',
  });
  const summary = useMemo(() => {
    if (!watchedSelections) return { totalQty: 0, totalProceedsAmt: 0 };
    let totalQty = 0;
    let totalProceedsAmt = 0;
    watchedSelections.forEach((sel) => {
      totalQty += sel.qtyToSell ?? 0;
      totalProceedsAmt += (sel.qtyToSell ?? 0) * (sel.unitProceeds ?? 0);
    });
    return { totalQty, totalProceedsAmt };
  }, [watchedSelections]);

  // In multi-listing mode the table is the source of truth for
  // totalNetProceeds — sync it whenever the summed rows change so the
  // downstream Earnings section (RetailSaleEarningsSection) and submit
  // path see a consistent number without the seller having to enter it
  // manually on the buyer step.
  useEffect(() => {
    setValue('totalNetProceeds', summary.totalProceedsAmt, {
      shouldDirty: false,
    });
  }, [setValue, summary.totalProceedsAmt]);

  const hasRows = fields.length > 0;

  return (
    <Stack direction="column" gap="l">
      {selectedBuyer && (
        <DetailSection name={customerInfoMsg}>
          <SectionContent numOfColumns={1}>
            <BuyerCard buyer={selectedBuyer} />
          </SectionContent>
        </DetailSection>
      )}

      <DetailSection name={<Content id={ContentId.Tickets} />}>
        {isLoading && <span className={styles.loadingText}>{loadingMsg}</span>}

        {!isLoading && !hasRows && (
          <EmptySectionContent.Root>
            <EmptySectionContent.Label>
              {noListingsMsg}
            </EmptySectionContent.Label>
          </EmptySectionContent.Root>
        )}

        {!isLoading && hasRows && (
          <div className={styles.tableScrollContainer}>
            <table className={styles.ticketTable}>
              <thead className={styles.stickyHeaderRow}>
                <tr>
                  <th className={styles.tableHeader}>{eventMsg}</th>
                  <th className={styles.tableHeader}>{listingMsg} #</th>
                  <th className={styles.tableHeader}>{sectionMsg}</th>
                  <th className={styles.tableHeader}>{rowMsg}</th>
                  <th className={styles.tableHeader}>{seatsMsg}</th>
                  <th className={styles.tableHeader}>{qtyMsg}</th>
                  <th className={styles.tableHeader}>{unitCostMsg}</th>
                  <th className={styles.tableHeader}>{unitProceedsMsg}</th>
                  <th className={styles.tableHeader}>{lineTotalMsg}</th>
                  <th className={styles.tableHeader} aria-label={removeMsg} />
                </tr>
              </thead>
              <tbody>
                {fields.map((field, idx) => (
                  <MultiListingTicketRow
                    key={field.id}
                    index={idx}
                    listingDetails={listingDetailsById[field.listingId]}
                    eventsTransformed={eventsTransformed}
                    currencyCode={currencyCode}
                    disabled={disabled}
                    onRemove={() => remove(idx)}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.summaryRow}>
                  <td className={styles.summaryCell} colSpan={5}>
                    {totalQuantityMsg}
                  </td>
                  <td className={styles.summaryCell}>{summary.totalQty}</td>
                  <td className={styles.summaryCell} colSpan={2}>
                    {totalProceedsMsg}
                  </td>
                  <td className={styles.summaryCell}>
                    {formatCurrency(summary.totalProceedsAmt, currencyCode)}
                  </td>
                  <td className={styles.summaryCell} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </DetailSection>

      <RetailSaleEarningsSection disabled={disabled} />

      <RetailSalePaymentSection />
    </Stack>
  );
};

/**
 * A single editable row in the multi-listing checkout table. Owns its own
 * `useSeatAutofill` subscription against the nested `perListingSelections.N`
 * paths so react-hook-form manages the seat-recompute reactively.
 */
type MultiListingTicketRowProps = {
  index: number;
  listingDetails: RetailSaleListingTickets | undefined;
  eventsTransformed: ReturnType<
    typeof useCatalogDataContextEventsTransformedSelector
  >;
  currencyCode: string;
  disabled: boolean;
  onRemove: () => void;
};

const MultiListingTicketRow = ({
  index,
  listingDetails,
  eventsTransformed,
  currencyCode,
  disabled,
  onRemove,
}: MultiListingTicketRowProps) => {
  const { control, setValue, register } =
    useFormContext<RetailSaleFormValues>();
  const removeMsg = useContent(ContentId.Remove);

  // Same reason as the parent — nested field-array reads need useWatch
  // to fire on setValue writes.
  const row = useWatch({
    control,
    name: `perListingSelections.${index}`,
  }) as MultiListingSelectionRow | undefined;

  // Restrict the ticket pool to just this row's (listing + section + row)
  // group. `useSeatAutofill` recomputes seatFrom/seatTo from this pool.
  const groupTickets = useMemo<Ticket[] | undefined>(() => {
    if (!listingDetails?.tickets || !row) return undefined;
    const ldSection = listingDetails.seating?.section ?? null;
    return listingDetails.tickets.filter((t) => {
      // Section is a listing-level property (only one section per listing);
      // row is a per-ticket property that falls back to the listing seating.
      const tRow = t.row ?? listingDetails.seating?.row ?? null;
      return (row.section ?? null) === ldSection && (row.row ?? null) === tRow;
    });
  }, [listingDetails, row]);

  const { seatFromToOptions } = useSeatAutofill({
    tickets: groupTickets,
    quantitySold: row?.qtyToSell,
    listing: listingDetails,
    section: row?.section ?? null,
    seatFromPath: `perListingSelections.${index}.seatFrom`,
    seatToPath: `perListingSelections.${index}.seatTo`,
    quantitySoldPath: `perListingSelections.${index}.qtyToSell`,
  });

  const maxAvailable = row?.availableTicketIds.length ?? 0;

  const catalogEvent = useMemo(
    () =>
      eventsTransformed?.find(
        (e) =>
          listingDetails != null &&
          e.event?.virtId === listingDetails.viagVirtId
      ),
    [eventsTransformed, listingDetails]
  );
  const { venue } = getPerformerAndVenueForEvent(
    catalogEvent?.event ?? null,
    undefined
  );
  const eventName = catalogEvent?.event?.name ?? venue?.name ?? '—';
  const eventUrl = catalogEvent?.event?.viagId
    ? `/inventory/event/${catalogEvent.event.viagId}`
    : null;

  const unitCostAmt = listingDetails?.unitCst?.amt ?? null;
  // Unit proceeds is now an editable per-row form field (POS-8649). Seeded
  // in RetailSaleActions from listPrice → allInPrice → unitCst.amt so the
  // seller sees a sensible starting value even for listings with no
  // list price set; free to override inline.
  const qty = row?.qtyToSell ?? 0;
  const unitProceedsAmt = row?.unitProceeds ?? 0;
  const lineTotal = qty * unitProceedsAmt;

  if (!row) return null;

  return (
    <tr>
      <td
        className={`${styles.tableCell} ${styles.eventCell}`}
        title={eventName}
      >
        <span className={styles.eventNameLine}>
          {eventName}
          {eventUrl && (
            <Link
              to={eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.eventLink}
              aria-label={eventName}
            >
              <OpenLinkIcon
                size={vars.iconSize.xs}
                fill={vars.color.textBrand}
              />
            </Link>
          )}
        </span>
      </td>
      <td className={styles.tableCell}>
        <span className={styles.listingIdCell}>
          {row.listingId}
          <Link
            to={getListingDetailsRelativeUrl(row.listingId)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.listingIdLink}
            aria-label={`${row.listingId}`}
          >
            <OpenLinkIcon size={vars.iconSize.xs} fill={vars.color.textBrand} />
          </Link>
        </span>
      </td>
      <td className={styles.tableCell}>{row.section ?? '—'}</td>
      <td className={styles.tableCell}>{row.row ?? '—'}</td>
      <td className={styles.tableCell}>
        <span className={styles.seatsCell}>
          <PosTextField
            spellCheck={false}
            disabled={disabled || !seatFromToOptions}
            className={styles.seatFromInput}
            {...register(`perListingSelections.${index}.seatFrom`)}
            onChange={(e) => {
              const typed = (e.target as HTMLInputElement).value;
              setValue(`perListingSelections.${index}.seatFrom`, typed, {
                shouldDirty: true,
              });
              // If the typed seat matches a valid consecutive-start ticket
              // in this group, auto-update seatTo to preserve the qty span.
              // If it doesn't match, leave seatTo alone — the user is still
              // typing / cleared the field.
              const fromTick = groupTickets?.find((t) => t.seat === typed);
              if (fromTick && seatFromToOptions && fromTick.lstOrd != null) {
                const toOrd = seatFromToOptions[fromTick.lstOrd];
                const toTick = groupTickets?.find((t) => t.lstOrd === toOrd);
                if (toTick) {
                  setValue(
                    `perListingSelections.${index}.seatTo`,
                    toTick.seat,
                    { shouldDirty: true }
                  );
                }
              }
            }}
          />
          {row.seatTo && row.seatTo !== row.seatFrom && (
            <span className={styles.seatToDisplay}>– {row.seatTo}</span>
          )}
        </span>
      </td>
      <td className={styles.tableCell}>
        <PosTextField
          type="number"
          inputMode="numeric"
          disabled={disabled || maxAvailable <= 0}
          className={styles.qtyInput}
          {...register(`perListingSelections.${index}.qtyToSell`, {
            valueAsNumber: true,
          })}
          onChange={(e) => {
            const raw = parseInt((e.target as HTMLInputElement).value, 10);
            // Clamp to [0, maxAvailable]; NaN → 0 so the input can be
            // cleared without corrupting the form state.
            const clamped = Number.isNaN(raw)
              ? 0
              : Math.max(0, Math.min(maxAvailable, raw));
            setValue(`perListingSelections.${index}.qtyToSell`, clamped, {
              shouldDirty: true,
            });
          }}
        />
      </td>
      <td className={styles.tableCell}>
        {unitCostAmt != null ? formatCurrency(unitCostAmt, currencyCode) : '—'}
      </td>
      <td className={styles.tableCell}>
        <PosTextField
          type="number"
          inputMode="decimal"
          disabled={disabled}
          className={styles.unitProceedsInput}
          {...register(`perListingSelections.${index}.unitProceeds`, {
            valueAsNumber: true,
          })}
          onChange={(e) => {
            const raw = parseFloat((e.target as HTMLInputElement).value);
            const next = Number.isFinite(raw) && raw >= 0 ? raw : 0;
            setValue(`perListingSelections.${index}.unitProceeds`, next, {
              shouldDirty: true,
            });
          }}
        />
      </td>
      <td className={styles.tableCell}>
        {formatCurrency(lineTotal, currencyCode)}
      </td>
      <td className={styles.tableCell}>
        <Button
          variant="text"
          size="unset"
          className={styles.removeButton}
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeMsg}
        >
          <DeleteIcon size={vars.iconSize.s} fill={IconsFill.currentColor} />
        </Button>
      </td>
    </tr>
  );
};

// Re-export the row for tests / external consumers if needed.
export { MultiListingTicketRow };
